import type { RootState } from '@/store'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setError, setLoading } from '@/store/slices/uiSlice'
import { OrderFormInfoData } from '@/types/RequisitionFormTypes'
import { extractFormOrderData } from '@/utils/FormDataExtraction'
import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import BaseOrderFormInfo from './BaseOrderFormInfo'

const OrderFormInfo: React.FC = () => {
  const location = useLocation()
  const state = (location.state as string) || ''
  const dispatch = useAppDispatch()
  const { loading, errors } = useAppSelector((state: RootState) => state.ui)
  const [data, setData] = useState<OrderFormInfoData | null>(null)
  const hasFetched = useRef(false)

  useEffect(() => {
    // Prevent duplicate API calls
    if (hasFetched.current) {
      return
    }

    const fetchFormInfo = async () => {
      hasFetched.current = true
      dispatch(setLoading({ type: 'formData', loading: true }))
      dispatch(setError({ type: 'formData', error: null }))
      try {
        const transformedData = await extractFormOrderData(state)
        // Store the fetched data in Redux - we'll need to create a new action for this
        // For now, we'll pass it directly to BaseOrderFormInfo
        setData(transformedData)
      } catch (err) {
        console.error('API call failed:', err)
        dispatch(
          setError({ type: 'formData', error: err instanceof Error ? err.message : 'Failed to fetch form information' })
        )
      } finally {
        dispatch(setLoading({ type: 'formData', loading: false }))
      }
    }

    fetchFormInfo()
  }, [state, dispatch])

  return <BaseOrderFormInfo data={data} loading={loading.formData} error={errors.formData} />
}

export default OrderFormInfo
